import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ArrowRight, KeyRound, ShieldCheck, Mail, Lock, User, Sparkles } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1676746424139-77f8bd8922a8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const [mode, setMode] = useState("login"); // login | register
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Şifremi Unuttum Modal State
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Giriş başarılı! Yönetim paneline yönlendiriliyorsunuz.");
      } else {
        await register(name, email, password);
        toast.success("Hesap başarıyla oluşturuldu.");
      }
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Giriş yapılırken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return toast.error("E-posta adresinizi giriniz.");
    setForgotLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success(res.data.message || "Şifre sıfırlama talebi alındı.");
      setForgotOpen(false);
      setForgotEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setForgotLoading(false);
    }
  };

  const fillAdminCredentials = () => {
    setEmail("admin@10-11.com.tr");
    setPassword("admin123");
    toast.info("Yönetici giriş bilgileri dolduruldu.");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#090d16] text-foreground mesh-bg">
      {/* Left: brand hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden border-r border-slate-800/60">
        <img src={HERO} alt="Saha" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#090d16] via-black/80 to-black/60" />
        
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 flex items-center gap-3"
        >
          <div className="w-14 h-14 rounded-full bg-slate-900 p-1 ring-2 ring-emerald-500/40">
            <img src="/assets/logo-round.webp" alt="10-11" className="w-full h-full object-cover rounded-full" />
          </div>
          <div>
            <span className="font-anton text-2xl tracking-wide text-white block leading-none">10-11</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 font-semibold">Yönetim Otomasyonu</span>
          </div>
        </motion.div>

        <div className="relative z-10 max-w-lg">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-xs uppercase tracking-widest font-semibold text-emerald-400">10-11.com.tr Entegre</span>
          </div>
          {["YÖNETİM", "KONTROL", "MERKEZİ"].map((w, i) => (
            <div key={i} className="overflow-hidden">
              <motion.h1
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 + i * 0.09, ease: [0.22, 1, 0.36, 1] }}
                className="font-anton text-5xl xl:text-6xl leading-[0.95] text-white tracking-wider"
              >
                {i === 2 ? <span className="gradient-text">{w}</span> : w}
              </motion.h1>
            </div>
          ))}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-6 text-slate-400 text-base leading-relaxed"
          >
            Saha takibi, slot videoları, gelir/gider hesabı ve otomatik e-posta bildirimleri — hepsi tek ekranda.
          </motion.p>
        </div>

        <div className="relative z-10 text-slate-500 text-xs flex items-center justify-between border-t border-slate-800/80 pt-4">
          <span>10-11.com.tr · Operasyon Merkezi</span>
          <span className="text-emerald-500/80 font-medium">v2.0 Canlı Sistem</span>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md space-y-6"
        >
          <div className="lg:hidden flex flex-col items-center gap-3 mb-6">
            <div className="w-20 h-20 rounded-full bg-slate-900 p-1 ring-2 ring-emerald-500/40">
              <img src="/assets/logo-round.webp" alt="10-11" className="w-full h-full object-cover rounded-full" />
            </div>
            <span className="font-anton text-3xl tracking-wide text-white">10-11</span>
          </div>

          <Card className="glass-card p-8 rounded-3xl border border-slate-800/80 shadow-2xl">
            <div className="mb-6">
              <h2 className="font-anton text-3xl sm:text-4xl text-white tracking-wide">
                {mode === "login" ? "PANEL GİRİŞİ" : "HESAP OLUŞTUR"}
              </h2>
              <p className="text-slate-400 text-xs mt-1">
                {mode === "login" ? "Devam etmek için hesabınıza giriş yapın." : "10-11 Yönetim Sistemine katılın."}
              </p>
            </div>

            {/* Hızlı Yönetici Giriş Kartı */}
            <div className="mb-6 p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                <div className="text-[11px] leading-tight">
                  <p className="font-bold text-emerald-300">Yönetici Giriş Bilgileri</p>
                  <p className="text-slate-400">admin@10-11.com.tr / admin123</p>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={fillAdminCredentials}
                className="h-7 text-[10px] border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 whitespace-nowrap"
              >
                Otomatik Doldur
              </Button>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <AnimatePresence mode="popLayout">
                {mode === "register" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-1.5"
                  >
                    <Label htmlFor="name" className="text-xs text-slate-300 flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-emerald-400" /> Ad Soyad
                    </Label>
                    <Input
                      id="name"
                      data-testid="register-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required={mode === "register"}
                      placeholder="Adınız Soyadınız"
                      className="h-11 bg-slate-900/80 border-slate-800 focus-visible:ring-emerald-500 text-sm"
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs text-slate-300 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-emerald-400" /> E-posta Adresi
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="admin@10-11.com.tr"
                  className="h-11 bg-slate-900/80 border-slate-800 focus-visible:ring-emerald-500 text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-xs text-slate-300 flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5 text-emerald-400" /> Şifre
                  </Label>
                  {mode === "login" && (
                    <button
                      type="button"
                      onClick={() => {
                        setForgotEmail(email);
                        setForgotOpen(true);
                      }}
                      className="text-[11px] font-medium text-emerald-400 hover:underline"
                    >
                      Şifremi Unuttum?
                    </button>
                  )}
                </div>
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="h-11 bg-slate-900/80 border-slate-800 focus-visible:ring-emerald-500 text-sm"
                />
              </div>

              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full h-11 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-semibold shadow-lg shadow-emerald-500/25 group transition-all mt-2"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <span className="flex items-center gap-2">
                    {mode === "login" ? "Sisteme Giriş Yap" : "Hesabımı Oluştur"}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t border-slate-800/60 text-center">
              <p className="text-xs text-slate-400">
                {mode === "login" ? "Henüz hesabınız yok mu?" : "Zaten hesabınız var mı?"}{" "}
                <button
                  type="button"
                  data-testid="toggle-auth-mode"
                  onClick={() => setMode(mode === "login" ? "register" : "login")}
                  className="text-emerald-400 font-bold hover:underline ml-1"
                >
                  {mode === "login" ? "Kayıt Ol" : "Giriş Yap"}
                </button>
              </p>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Şifremi Unuttum Modalı */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 text-foreground sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="font-anton text-2xl flex items-center gap-2 text-white">
              <KeyRound className="w-5 h-5 text-emerald-400" /> ŞİFREMİ UNUTTUM
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleForgotPassword} className="space-y-4 py-2">
            <p className="text-xs text-slate-400 leading-relaxed">
              Hesabınıza ait e-posta adresinizi giriniz. Şifre sıfırlama bilgileri sistem yöneticilerine ve e-posta adresinize iletilecektir.
            </p>

            <div className="space-y-2">
              <Label htmlFor="forgot-email" className="text-xs">E-posta Adresi</Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="ornek@10-11.com.tr"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                className="bg-slate-900 border-slate-800"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setForgotOpen(false)} className="border-slate-800 text-slate-300">İptal</Button>
              <Button type="submit" disabled={forgotLoading} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Şifremi Sıfırla & Gönder"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

